/**
 * Return a new array that has been filtered according to filtering rules and sorted by the selected keys
 *
 * NOTICE! The returned array is a shallow copy!
 *
 *
 * @param {*} targetArray
 * @param {*} sorterOptions
 */
export default function arraySorter(targetArray, sortingOptions = null) {
    if (!Array.isArray(targetArray)) {
        console.warn("Cannot sort non array values.", targetArray);
        return targetArray;
    }

    if (targetArray.length === 0) {
        return targetArray.slice();
    }

    if (typeof targetArray[0] !== "object") {
        return targetArray.slice().sort(sortByValue);
    }

    if (sortingOptions === null) {
        return targetArray.slice();
    }

    if (!Array.isArray(sortingOptions)) {
        sortingOptions = [sortingOptions];
    }

    return targetArray.slice().sort((a, b) => {
        return sorterRecursion(a, b, sortingOptions, 0);
    });
}

/**
 * Start sorting from the first column and go deeper if the result is equal
 */
function sorterRecursion(a, b, options, index) {
    if (options[index] === undefined) {
        return false;
    }
    const result = sortingColumn(a, b, options[index]);

    if (result === null) {
        return sorterRecursion(a, b, options, index + 1);
    }

    return result;
}

/**
 * Sort by single rule set.
 */
function sortingColumn(a, b, rules) {

    if (typeof rules === "string") {
        return ruleIsObjectKey(a, b, rules);
    }

    if (typeof rules === "object" && rules.key !== undefined) {
        if(rules.fn !== undefined && typeof rules.fn === "function") {
            return rulesIsObjectKeyWithCustomFunction(a, b, rules.key, rules.fn, rules.reverse, rules.dotNotation);
        }
        return ruleIsObjectKey(a, b, rules.key, rules.reverse, rules.dotNotation);
    }
    if (typeof rules === "function") {
        return ruleIsAFunction(a, b, rules);
    }

    console.error("rulesObject is invalid", rules);
}

/**
 * Rule contains a custom function that is used for sorting
 *
 * This function can either directly return a boolean value for sorting purposes or a rulesObject
 */
function ruleIsAFunction(a, b, rules) {
    const ruleObj = rules(a, b);

    // Function returns a rule object
    if (typeof ruleObj === "object" && ruleObj.key !== undefined) {
        return ruleIsObjectKey(a, b, ruleObj.key, ruleObj.reverse, ruleObj.dotNotation);
    }

    // Function itself is a sorter function (aka returns a boolean value)
    if (typeof ruleObj === "boolean") {
        return ruleObj;
    }

    console.warn(
        "Custom sorter function rule returns invalid value. Sorter function should either return a boolean value for sorting or sortingRuleObject.",
        ruleObj
    );
}

/**
 * The rule for this sorter
 */
function ruleIsObjectKey(a, b, key, reverse = false, dotNotation = true) {
    if (key.includes(".") && dotNotation) {
        return ruleIsDotNotation(a, b, key, reverse);
    }

    return sortByValue(a[key], b[key], reverse);
}

function rulesIsObjectKeyWithCustomFunction(a, b, key, customFunction, reverse = false, dotNotation = true) {
    if(key.includes(".") && dotNotation) {
        // TODO...
    }



    return customFunction(a[key], b[key], reverse);



}

/**
 * The value of for the sorting is deeper in the object tree and it is retrieved with str.str.str... key string
 */
function ruleIsDotNotation(a, b, key, reverse = false) {
    function findValue(object, keys, depth) {
        if (object[keys[depth]] === undefined) {
            return object;
        }
        return findValue(object[keys[depth]], keys, depth + 1);
    }

    const valA = findValue(a, key.split("."), 0);
    const valB = findValue(b, key.split("."), 0);

    return sortByValue(valA, valB, reverse);
}

/**
 * Select a the comparison method based on the types of the value
 *
 * The type of 'a' defines the comparison method used
 */
function sortByValue(a, b, reverse = false) {
    if (typeof a !== typeof b) {
        // console.warn("Type mismatch between compared values: \n\ta:\t",a, typeof a, "\n\tb:\t", b, typeof b);
    }

    if (!isNaN(a) && !isNaN(b)) {
        return numberComparison(a, b, reverse);
    }

    if (Array.isArray(a) && Array.isArray(b)) {
        return arrayLengthComparison(a, b, reverse);
    }

    if (typeof a === "string" && typeof b === "string") {
        return stringComparison(a, b, reverse);
    }

    return defaultCompare(a, b, reverse);
}

function defaultCompare(a, b, reverse) {
    if (a === b) {
        return null;
    }

    if (reverse) {
        return a < b;
    }

    return a > b;
}

/**
 * Compare number values
 */
function numberComparison(a, b, reverse) {
    if (a - b === 0) {
        return null;
    }

    if (reverse) {
        return b - a;
    }
    return a - b;
}

/**
 * Compare NaN values
 */
function stringComparison(a, b, reverse) {
    if (a === b) {
        return null;
    }

    if (reverse) {
        // return a < b;
        return b.localeCompare(a);
    }

    return a.localeCompare(b);
}

function arrayLengthComparison(a, b, reverse) {
    if (!Array.isArray(a)) {
        return false;
    }

    if (!Array.isArray(b)) {
        return false;
    }

    if (reverse) {
        return a.length < b.length;
    }
    return a.length > b.length;
}
